import { makeAutoObservable } from 'mobx';
import { CONTENT_TYPE, METHOD } from '../constants/method';
import { callApi } from '../util/apiWrapper';
import { dateUtcToS } from '../util/dateUtil';
import { prepareSearchParam } from '../util/miscUtil';

const BASE_URL = process.env.REACT_APP_BASE_URL;

class FileHostStore {
  state = {
    loading: false,
    scormFiles: [],
    totalFiles: 0,
    page: 1,
    perPage: 10,
    error: null,
  };

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    this.loadState();
  }

  /**
   * Helper method to cleanly set the error state and log it
   * @param {string} errorMessage
   */
  _setError(errorMessage) {
    this.setStateField('error', errorMessage);
  }

  /**
   * Generic reusable method to handle loading indicator and state updates
   * @param {boolean} isLoading The loading state
   * @param {object} [updatedState] Optional--additional state to update after loading
   */
  _setLoadingAndState(isLoading, updatedState = {}) {
    this.setState({ loading: isLoading, ...updatedState });
  }

  /**
   * Helper function to process and map file data to a consistent format
   * @param {object[]} files File data array from API
   * @returns {object[]} Mapped file data
   */
  _mapFileData(files) {
    return files.map((file) => ({
      name: file.file_name,
      id: file.id,
      type: file.file_type,
      uploadedBy: file.uploaded_by,
      uploadedAt: dateUtcToS(file?.uploaded_at),
      url: BASE_URL + 'resources?id=' + file.id,
      size: file?.file_size,
    }));
  }

  /**
   * Helper function to merge state updates while saving to localStorage
   * @param {object} newState New state values
   */
  _saveStateWithMerge(newState) {
    this.state = { ...this.state, ...newState };
    this.saveState();
  }

  /**
   * Single state field modification with saving
   * @param {string} key State key to update
   * @param {any} value The value to set for the key
   */
  setStateField(key, value) {
    this._saveStateWithMerge({ [key]: value });
  }

  /**
   * Merges multiple state fields with saving
   * @param {object} newState Object containing new state fields
   */
  setState(newState) {
    this._saveStateWithMerge(newState);
  }

  /**
   * Retrieve scormFiles from the backend API
   * @param {object} authStore Store holding authentication details
   * @param {number} page Page of results to fetch
   * @param {number} perPage Files per page to fetch
   * @param {string} orderBy
   * @param {string} orderDirection
   * @param {object} filters
   */
  async retrieve(authStore, page = this.state.page, perPage = this.state.perPage, orderBy = '', orderDirection = '', filters = {}) {
    this._setLoadingAndState(true);
    try {
      const filterForRequest = prepareSearchParam(filters);
      const data = await callApi(authStore, `/resources?page=${page}&perPage=${perPage}&orderBy=${orderBy}&orderDirection=${orderDirection}${filterForRequest}`, METHOD.GET, null, CONTENT_TYPE.APPLICATION_JSON);

      this._setLoadingAndState(false, {
        scormFiles: this._mapFileData(data?.data || data?.Result || []),
        totalFiles: data?.total || data?.Total || 0,
        page,
        perPage,
        error: null,
      });
    } catch (error) {
      this._setError(error.message || 'Failed to retrieve files.');
      this.setStateField('loading', false);
    }
  }

  /**
   * Add a new course file
   * @param {object} authStore Store holding authentication details
   * @param {object} fileData Data for the new file
   */
  async addCourse(authStore, fileData) {
    try {
      const data = await callApi(authStore, `/resources`, METHOD.POST, fileData);

      this.setStateField('scormFiles', [...this.state.scormFiles, ...this._mapFileData([data])]);

      this.setStateField('totalFiles', this.state.totalFiles + 1);
    } catch (error) {
      this._setError(error.message || 'Failed to add course.');
      throw error;
    }
  }

  /**
   * Delete an existing course file
   * @param {object} authStore Store holding authentication details
   * @param {string} fileId The ID of the file to delete
   */
  async deleteCourse(authStore, fileId) {
    try {
      await callApi(authStore, `/resources/${fileId}`, METHOD.DELETE, null, CONTENT_TYPE.APPLICATION_JSON);

      this.setStateField(
        'scormFiles',
        this.state.scormFiles.filter((file) => file.id !== fileId),
      );

      this.setStateField('totalFiles', this.state.totalFiles - 1);
    } catch (error) {
      this._setError(error.message || 'Failed to delete course.');
    }
  }

  resetState() {
    this._saveStateWithMerge({
      loading: false,
      scormFiles: [],
      totalFiles: 0,
      page: 1,
      perPage: 10,
      error: null,
    });
  }

  saveState() {
    localStorage.setItem('FileHostStore', JSON.stringify(this.state));
  }

  loadState() {
    const savedState = JSON.parse(localStorage.getItem('FileHostStore'));
    if (savedState) {
      this.state = savedState;
    }
  }

  deleteStore() {
    this.resetState();
    localStorage.removeItem('FileHostStore');
  }
}

export default FileHostStore;
